import React, { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Badge,
  Table,
  Tabs,
  Loader,
  Alert,
  Grid,
  ActionIcon,
  Modal,
  FileInput,
  Select,
  NumberInput,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconArrowLeft, IconEdit, IconTrash, IconUpload, IconExternalLink, IconRefresh } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useContract,
  useContractAuditLogs,
  useContractConsumptions,
  useContractExtensions,
  useContractRevisions,
  useCreateContractExtension,
  useCreateContractRevision,
  useDeleteContract,
  useUpdateConsumedValue,
  useUpdateContractConsumption,
} from '../hooks/useContracts';
import {
  useContractDocuments,
  useDownloadDocument,
  useReplaceDocument,
  useUpdateDocumentMetadata,
  useUploadDocument,
} from '../hooks/useContractDocuments';
import { useContractAlerts, useAcknowledgeAlert } from '../hooks/useContractAlerts';
import { ContractStatusBadge } from '../components/ContractStatusBadge';
import { ContractTypeBadge } from '../components/ContractTypeBadge';
import { formatCurrency, getCurrencySymbol } from '../../../utils/currency';
import { ContractConsumption, ContractDocument } from '../../../api/contracts';
import dayjs from 'dayjs';

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'PURCHASE_ORDER', label: 'Purchase Order' },
  { value: 'ANNEX', label: 'Annex' },
  { value: 'AMENDMENT', label: 'Amendment' },
  { value: 'CONTRACT_EXTENSION', label: 'Contract Extension' },
  { value: 'CONTRACT_REVISION', label: 'Contract Revision' },
  { value: 'OTHER', label: 'Other' },
];

export function ContractDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: contract, isLoading } = useContract(id || '');
  const { data: consumptions } = useContractConsumptions(id || '');
  const { data: documents } = useContractDocuments(id || '');
  const { data: auditLogs } = useContractAuditLogs(id || '');
  const { data: extensions } = useContractExtensions(id || '');
  const { data: revisions } = useContractRevisions(id || '');
  const { data: alerts } = useContractAlerts(id);
  const deleteContract = useDeleteContract();
  const uploadDocument = useUploadDocument();
  const updateDocumentMetadata = useUpdateDocumentMetadata();
  const replaceDocument = useReplaceDocument();
  const downloadDocument = useDownloadDocument();
  const acknowledgeAlert = useAcknowledgeAlert();
  const updateConsumedValue = useUpdateConsumedValue();
  const updateConsumption = useUpdateContractConsumption();
  const createExtension = useCreateContractExtension();
  const createRevision = useCreateContractRevision();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('CONTRACT');
  const [documentDescription, setDocumentDescription] = useState('');
  const [editingDocument, setEditingDocument] = useState<ContractDocument | null>(null);
  const [editDocumentType, setEditDocumentType] = useState('CONTRACT');
  const [editDocumentDescription, setEditDocumentDescription] = useState('');
  const [replacingDocument, setReplacingDocument] = useState<ContractDocument | null>(null);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [consumedValueModalOpen, setConsumedValueModalOpen] = useState(false);
  const [consumeAmount, setConsumeAmount] = useState<number>(0);
  const [consumePoNumber, setConsumePoNumber] = useState('');
  const [purchaseOrderFile, setPurchaseOrderFile] = useState<File | null>(null);
  const [editingConsumption, setEditingConsumption] = useState<ContractConsumption | null>(null);
  const [editConsumptionAmount, setEditConsumptionAmount] = useState<number>(0);
  const [editConsumptionPoNumber, setEditConsumptionPoNumber] = useState('');
  const [editConsumptionFile, setEditConsumptionFile] = useState<File | null>(null);
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [extensionDays, setExtensionDays] = useState<number | ''>('');
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionFile, setExtensionFile] = useState<File | null>(null);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [revisionValue, setRevisionValue] = useState<number>(0);
  const [revisionReason, setRevisionReason] = useState('');
  const [revisionFile, setRevisionFile] = useState<File | null>(null);

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Loader />
      </Container>
    );
  }

  if (!contract) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red">Contract not found</Alert>
      </Container>
    );
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      await deleteContract.mutateAsync(contract.id);
      navigate('/contracts');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !id) return;
    await uploadDocument.mutateAsync({
      contractId: id,
      file: uploadFile,
      data: { documentType, description: documentDescription || undefined },
    });
    setUploadModalOpen(false);
    setUploadFile(null);
    setDocumentDescription('');
  };

  const handleDownload = async (docId: string) => {
    await downloadDocument.mutateAsync(docId);
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    await acknowledgeAlert.mutateAsync({ contractId: contract.id, alertId });
  };

  const handleUpdateConsumedValue = async () => {
    if (!id || !purchaseOrderFile || consumeAmount <= 0 || !consumePoNumber.trim()) return;

    const uploadedPoDoc = await uploadDocument.mutateAsync({
      contractId: id,
      file: purchaseOrderFile,
      data: { documentType: 'PURCHASE_ORDER' },
    });

    await updateConsumedValue.mutateAsync({
      id,
      amount: consumeAmount,
      poNumber: consumePoNumber.trim(),
      contractDocumentId: uploadedPoDoc.id,
    });

    setConsumedValueModalOpen(false);
    setConsumeAmount(0);
    setConsumePoNumber('');
    setPurchaseOrderFile(null);
  };

  const handleOpenConsumedValueModal = () => {
    setConsumeAmount(0);
    setConsumePoNumber('');
    setPurchaseOrderFile(null);
    setConsumedValueModalOpen(true);
  };

  const handleOpenDocumentEdit = (doc: ContractDocument) => {
    setEditingDocument(doc);
    setEditDocumentType(doc.documentType);
    setEditDocumentDescription(doc.description || '');
  };

  const handleSaveDocumentMetadata = async () => {
    if (!id || !editingDocument) return;

    await updateDocumentMetadata.mutateAsync({
      contractId: id,
      documentId: editingDocument.id,
      data: {
        documentType: editDocumentType,
        description: editDocumentDescription || null,
      },
    });
    setEditingDocument(null);
  };

  const handleOpenDocumentReplace = (doc: ContractDocument) => {
    setReplacingDocument(doc);
    setEditDocumentType(doc.documentType);
    setEditDocumentDescription(doc.description || '');
    setReplacementFile(null);
  };

  const handleReplaceDocument = async () => {
    if (!id || !replacingDocument || !replacementFile) return;

    await replaceDocument.mutateAsync({
      contractId: id,
      documentId: replacingDocument.id,
      file: replacementFile,
      data: {
        documentType: editDocumentType,
        description: editDocumentDescription || null,
      },
    });
    setReplacingDocument(null);
    setReplacementFile(null);
  };

  const handleOpenConsumptionEdit = (item: ContractConsumption) => {
    setEditingConsumption(item);
    setEditConsumptionAmount(item.amount);
    setEditConsumptionPoNumber(item.poNumber);
    setEditConsumptionFile(null);
  };

  const handleSaveConsumption = async () => {
    if (!id || !editingConsumption || editConsumptionAmount <= 0 || !editConsumptionPoNumber.trim()) return;

    let contractDocumentId = editingConsumption.contractDocumentId;
    if (editConsumptionFile) {
      const uploadedPoDoc = await uploadDocument.mutateAsync({
        contractId: id,
        file: editConsumptionFile,
        data: { documentType: 'PURCHASE_ORDER' },
      });
      contractDocumentId = uploadedPoDoc.id;
    }

    await updateConsumption.mutateAsync({
      id,
      consumptionId: editingConsumption.id,
      data: {
        amount: editConsumptionAmount,
        poNumber: editConsumptionPoNumber.trim(),
        contractDocumentId,
      },
    });
    setEditingConsumption(null);
    setEditConsumptionFile(null);
  };

  const handleOpenExtensionModal = () => {
    setExtensionDays('');
    setExtensionReason('');
    setExtensionFile(null);
    setExtensionModalOpen(true);
  };

  const extensionDaysNum = typeof extensionDays === 'number' ? extensionDays : 0;
  const contractInitialEndDate = contract.initialEndDate ?? contract.endDate;
  const calculatedExtensionEndDate =
    extensionDaysNum > 0 && contract.endDate
      ? dayjs(contract.endDate).add(extensionDaysNum, 'day')
      : null;

  const handleCreateExtension = async () => {
    if (!id || extensionDaysNum < 1 || !extensionReason.trim() || !extensionFile) return;

    const referenceDocument = await uploadDocument.mutateAsync({
      contractId: id,
      file: extensionFile,
      data: { documentType: 'CONTRACT_EXTENSION', description: extensionReason.trim() },
    });

    await createExtension.mutateAsync({
      id,
      data: {
        extensionDays: extensionDaysNum,
        reason: extensionReason.trim(),
        referenceDocumentId: referenceDocument.id,
      },
    });
    setExtensionModalOpen(false);
    setExtensionDays('');
    setExtensionReason('');
    setExtensionFile(null);
  };

  const handleCreateRevision = async () => {
    if (!id || revisionValue <= 0 || !revisionReason.trim() || !revisionFile) return;

    const referenceDocument = await uploadDocument.mutateAsync({
      contractId: id,
      file: revisionFile,
      data: { documentType: 'CONTRACT_REVISION', description: revisionReason.trim() },
    });

    await createRevision.mutateAsync({
      id,
      data: {
        newContractValue: revisionValue,
        reason: revisionReason.trim(),
        referenceDocumentId: referenceDocument.id,
      },
    });
    setRevisionModalOpen(false);
    setRevisionValue(0);
    setRevisionReason('');
    setRevisionFile(null);
  };

  return (
    <Container size="xl" py="xl">
      <Group mb="xl" justify="space-between">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/contracts')}
          >
            Back
          </Button>
          <Title order={2}>{contract.contractNumber}</Title>
        </Group>
        <Group>
          <Button
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={() => navigate(`/contracts/${contract.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="light"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Group>
      </Group>

      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="documents">Documents</Tabs.Tab>
          <Tabs.Tab value="alerts">
            Alerts {alerts && alerts.length > 0 && `(${alerts.filter((a) => !a.isRead).length})`}
          </Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" pt="xl">
          <Grid>
            <Grid.Col span={12}>
              <Stack gap="md">
                <Paper shadow="sm" p="md">
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={500}>Contract Information</Text>
                      <Group>
                        <ContractStatusBadge status={contract.status} />
                        <ContractTypeBadge type={contract.type} />
                      </Group>
                    </Group>
                    <Grid>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Contract Title
                        </Text>
                        <Text fw={500}>{contract.title || '-'}</Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Supplier Name
                        </Text>
                        <Text fw={500}>{contract.supplierName}</Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Contract Number
                        </Text>
                        <Text fw={500}>{contract.contractNumber}</Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Start Date
                        </Text>
                        <Text fw={500}>
                          {contract.startDate
                            ? dayjs(contract.startDate).format('DD MMM YYYY')
                            : '—'}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Initial End Date
                        </Text>
                        <Text fw={500}>
                          {contractInitialEndDate
                            ? dayjs(contractInitialEndDate).format('DD MMM YYYY')
                            : '—'}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Current End Date
                        </Text>
                        <Text fw={500}>
                          {contract.endDate
                            ? dayjs(contract.endDate).format('DD MMM YYYY')
                            : '—'}
                        </Text>
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Paper>

                <Paper shadow="sm" p="md">
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={500}>Financial Information</Text>
                      <Group>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={handleOpenExtensionModal}
                          disabled={!contract.endDate}
                        >
                          Extend Contract
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            setRevisionValue(contract.contractValue);
                            setRevisionModalOpen(true);
                          }}
                        >
                          Revise Value
                        </Button>
                      </Group>
                    </Group>
                    <Grid>
                      <Grid.Col span={4}>
                        <Text size="sm" c="dimmed">
                          Contract Value
                        </Text>
                        <Text fw={500} size="lg">
                          {formatCurrency(contract.contractValue, contract.currency || 'MVR')}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Group justify="space-between" align="flex-start">
                          <div>
                            <Text size="sm" c="dimmed">
                              Consumed Value
                            </Text>
                            <Text fw={500} size="lg" c="orange">
                              {formatCurrency(contract.consumedValue, contract.currency || 'MVR')}
                            </Text>
                          </div>
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={handleOpenConsumedValueModal}
                            title="Consume value with purchase order"
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Group>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Text size="sm" c="dimmed">
                          Remaining Balance
                        </Text>
                        <Text fw={500} size="lg" c="green">
                          {formatCurrency(contract.remainingBalance, contract.currency || 'MVR')}
                        </Text>
                      </Grid.Col>
                    </Grid>
                    {contract.hasPriceCap && contract.priceCap && (
                      <Alert color="blue">
                        <Text size="sm">
                          Price Cap: {formatCurrency(contract.priceCap, contract.currency || 'MVR')} (Alert at{' '}
                          {contract.priceCapThreshold || 90}%)
                        </Text>
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Paper shadow="sm" p="md">
                  <Stack gap="md">
                    <Title order={4}>PO Consumption History</Title>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>PO Number / Document</Table.Th>
                          <Table.Th>Amount</Table.Th>
                          <Table.Th>Consumed Date</Table.Th>
                          <Table.Th>Uploaded By</Table.Th>
                          <Table.Th>Running Balance</Table.Th>
                          <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {consumptions && consumptions.length > 0 ? (
                          (() => {
                            let runningBalance = contract.contractValue;
                            return consumptions.map((item) => {
                              runningBalance -= item.amount;
                              return (
                                <Table.Tr key={item.id}>
                                  <Table.Td>
                                    <Stack gap={2}>
                                      <Text fw={500}>{item.poNumber}</Text>
                                      <Text size="xs" c="dimmed">
                                        {item.contractDocument?.fileName || 'Purchase Order'}
                                      </Text>
                                    </Stack>
                                  </Table.Td>
                                  <Table.Td>{formatCurrency(item.amount, contract.currency || 'MVR')}</Table.Td>
                                  <Table.Td>{dayjs(item.consumedAt).format('DD MMM YYYY, HH:mm')}</Table.Td>
                                  <Table.Td>{item.uploadedBy?.fullName || 'Unknown'}</Table.Td>
                                  <Table.Td>{formatCurrency(Math.max(0, runningBalance), contract.currency || 'MVR')}</Table.Td>
                                  <Table.Td>
                                    <Group gap="xs">
                                      <ActionIcon
                                        variant="light"
                                        onClick={() => handleDownload(item.contractDocumentId)}
                                        title="View document"
                                      >
                                        <IconExternalLink size={16} />
                                      </ActionIcon>
                                      <ActionIcon
                                        variant="light"
                                        color="orange"
                                        onClick={() => handleOpenConsumptionEdit(item)}
                                        title="Edit PO value"
                                      >
                                        <IconEdit size={16} />
                                      </ActionIcon>
                                    </Group>
                                  </Table.Td>
                                </Table.Tr>
                              );
                            });
                          })()
                        ) : (
                          <Table.Tr>
                            <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                              <Text c="dimmed">No PO consumption entries yet</Text>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="xl">
          <Paper shadow="sm" p="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>Documents</Title>
              <Button
                leftSection={<IconUpload size={16} />}
                onClick={() => setUploadModalOpen(true)}
              >
                Upload Document
              </Button>
            </Group>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Document Type</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>File Name</Table.Th>
                  <Table.Th>Uploaded At</Table.Th>
                  <Table.Th>Uploaded By</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents && documents.length > 0 ? (
                  documents.map((doc) => (
                    <Table.Tr key={doc.id}>
                      <Table.Td>{doc.documentType}</Table.Td>
                      <Table.Td>
                        <Badge variant="light">{doc.version}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={doc.isActive ? 'green' : 'gray'} variant="light">
                          {doc.isActive ? 'Active' : 'History'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{doc.fileName}</Table.Td>
                      <Table.Td>{dayjs(doc.uploadedAt).format('DD MMM YYYY')}</Table.Td>
                      <Table.Td>{doc.uploadedBy?.fullName || 'Unknown'}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="light"
                            onClick={() => handleDownload(doc.id)}
                            title="Open document"
                          >
                            <IconExternalLink size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="orange"
                            onClick={() => handleOpenDocumentEdit(doc)}
                            title="Edit metadata"
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="grape"
                            onClick={() => handleOpenDocumentReplace(doc)}
                            title="Replace file"
                          >
                            <IconRefresh size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={7} style={{ textAlign: 'center' }}>
                      <Text c="dimmed">No documents uploaded</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="alerts" pt="xl">
          <Paper shadow="sm" p="md">
            <Title order={4} mb="md">
              Alerts
            </Title>
            {alerts && alerts.length > 0 ? (
              <Stack gap="md">
                {alerts.map((alert) => (
                  <Alert
                    key={alert.id}
                    color={alert.alertType === 'EXPIRY' ? 'orange' : 'red'}
                    title={alert.alertType}
                    onClose={() => handleAcknowledgeAlert(alert.id)}
                    withCloseButton={!alert.isRead}
                  >
                    {alert.message}
                  </Alert>
                ))}
              </Stack>
            ) : (
              <Text c="dimmed">No alerts</Text>
            )}
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="history" pt="xl">
          <Stack gap="md">
            <Paper shadow="sm" p="md">
              <Title order={4} mb="md">
                Extensions
              </Title>
              {extensions && extensions.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Initial End Date</Table.Th>
                      <Table.Th>Previous End Date</Table.Th>
                      <Table.Th>Days Extended</Table.Th>
                      <Table.Th>New End Date</Table.Th>
                      <Table.Th>Reason</Table.Th>
                      <Table.Th>Changed By</Table.Th>
                      <Table.Th>Reference</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {extensions.map((extension) => (
                      <Table.Tr key={extension.id}>
                        <Table.Td>
                          {extension.initialEndDate ?? contractInitialEndDate
                            ? dayjs(extension.initialEndDate ?? contractInitialEndDate).format(
                                'DD MMM YYYY'
                              )
                            : '—'}
                        </Table.Td>
                        <Table.Td>{dayjs(extension.previousEndDate).format('DD MMM YYYY')}</Table.Td>
                        <Table.Td>
                          {dayjs(extension.newEndDate).diff(
                            dayjs(extension.previousEndDate),
                            'day'
                          )}
                        </Table.Td>
                        <Table.Td>{dayjs(extension.newEndDate).format('DD MMM YYYY')}</Table.Td>
                        <Table.Td>{extension.reason}</Table.Td>
                        <Table.Td>{extension.changedBy?.fullName || 'Unknown'}</Table.Td>
                        <Table.Td>
                          {extension.referenceDocumentId && (
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() => handleDownload(extension.referenceDocumentId!)}
                            >
                              Open
                            </Button>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed">No contract extensions recorded</Text>
              )}
            </Paper>

            <Paper shadow="sm" p="md">
              <Title order={4} mb="md">
                Revisions
              </Title>
              {revisions && revisions.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Previous Value</Table.Th>
                      <Table.Th>New Value</Table.Th>
                      <Table.Th>Reason</Table.Th>
                      <Table.Th>Changed By</Table.Th>
                      <Table.Th>Reference</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {revisions.map((revision) => (
                      <Table.Tr key={revision.id}>
                        <Table.Td>{formatCurrency(revision.previousContractValue, contract.currency || 'MVR')}</Table.Td>
                        <Table.Td>{formatCurrency(revision.newContractValue, contract.currency || 'MVR')}</Table.Td>
                        <Table.Td>{revision.reason}</Table.Td>
                        <Table.Td>{revision.changedBy?.fullName || 'Unknown'}</Table.Td>
                        <Table.Td>
                          {revision.referenceDocumentId && (
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() => handleDownload(revision.referenceDocumentId!)}
                            >
                              Open
                            </Button>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed">No contract revisions recorded</Text>
              )}
            </Paper>

            <Paper shadow="sm" p="md">
              <Title order={4} mb="md">
                Audit Logs
              </Title>
              {auditLogs && auditLogs.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Action</Table.Th>
                      <Table.Th>Entity</Table.Th>
                      <Table.Th>Changed By</Table.Th>
                      <Table.Th>Changed At</Table.Th>
                      <Table.Th>Reference</Table.Th>
                      <Table.Th>Reason</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {auditLogs.map((log) => (
                      <Table.Tr key={log.id}>
                        <Table.Td>
                          <Badge variant="light">{log.action}</Badge>
                        </Table.Td>
                        <Table.Td>{log.entityType}</Table.Td>
                        <Table.Td>{log.changedBy?.fullName || 'Unknown'}</Table.Td>
                        <Table.Td>{dayjs(log.createdAt).format('DD MMM YYYY, HH:mm')}</Table.Td>
                        <Table.Td>
                          {log.referenceDocumentId && (
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() => handleDownload(log.referenceDocumentId!)}
                            >
                              Open
                            </Button>
                          )}
                        </Table.Td>
                        <Table.Td>{log.reason || '-'}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed">No audit logs recorded</Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title="Upload Document"
      >
        <Stack gap="md">
          <Select
            label="Document Type"
            data={DOCUMENT_TYPE_OPTIONS}
            value={documentType}
            onChange={(value) => setDocumentType(value || 'CONTRACT')}
          />
          <Textarea
            label="Description"
            placeholder="Optional document description"
            value={documentDescription}
            onChange={(event) => setDocumentDescription(event.currentTarget.value)}
          />
          <FileInput
            label="File"
            placeholder="Select file"
            value={uploadFile}
            onChange={setUploadFile}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} loading={uploadDocument.isPending}>
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={consumedValueModalOpen}
        onClose={() => setConsumedValueModalOpen(false)}
        title="Consume Contract Value"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Enter the amount to consume from this contract and upload the related purchase order document.
          </Text>
          <NumberInput
            label="Amount to Consume"
            placeholder="Enter amount"
            value={consumeAmount}
            onChange={(value) => setConsumeAmount(Number(value) || 0)}
            min={0.01}
            decimalScale={2}
            prefix={`${getCurrencySymbol(contract.currency || 'MVR')} `}
            required
          />
          <TextInput
            label="PO Number"
            placeholder="Enter purchase order number"
            value={consumePoNumber}
            onChange={(e) => setConsumePoNumber(e.currentTarget.value)}
            required
          />
          <FileInput
            label="Purchase Order Document"
            placeholder="Upload purchase order file"
            value={purchaseOrderFile}
            onChange={setPurchaseOrderFile}
            required
          />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Current Consumed: {formatCurrency(contract.consumedValue, contract.currency || 'MVR')}
            </Text>
            <Text size="sm" c="dimmed">
              New Remaining:{' '}
              {formatCurrency(
                Math.max(0, contract.remainingBalance - consumeAmount),
                contract.currency || 'MVR'
              )}
            </Text>
          </Group>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConsumedValueModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateConsumedValue}
              loading={updateConsumedValue.isPending || uploadDocument.isPending}
              disabled={!purchaseOrderFile || consumeAmount <= 0 || !consumePoNumber.trim()}
            >
              Consume
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!editingDocument}
        onClose={() => setEditingDocument(null)}
        title="Edit Document Metadata"
      >
        <Stack gap="md">
          <Select
            label="Document Type"
            data={DOCUMENT_TYPE_OPTIONS}
            value={editDocumentType}
            onChange={(value) => setEditDocumentType(value || 'OTHER')}
            required
          />
          <Textarea
            label="Description"
            value={editDocumentDescription}
            onChange={(event) => setEditDocumentDescription(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditingDocument(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDocumentMetadata} loading={updateDocumentMetadata.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!replacingDocument}
        onClose={() => setReplacingDocument(null)}
        title="Replace Document File"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Replacing a file creates a new active version and keeps the old version in history.
          </Text>
          <Select
            label="Document Type"
            data={DOCUMENT_TYPE_OPTIONS}
            value={editDocumentType}
            onChange={(value) => setEditDocumentType(value || 'OTHER')}
            required
          />
          <Textarea
            label="Description"
            value={editDocumentDescription}
            onChange={(event) => setEditDocumentDescription(event.currentTarget.value)}
          />
          <FileInput
            label="Replacement File"
            placeholder="Select replacement file"
            value={replacementFile}
            onChange={setReplacementFile}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setReplacingDocument(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleReplaceDocument}
              loading={replaceDocument.isPending}
              disabled={!replacementFile}
            >
              Replace
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!editingConsumption}
        onClose={() => setEditingConsumption(null)}
        title="Edit PO Consumption"
      >
        <Stack gap="md">
          <NumberInput
            label="PO Amount"
            value={editConsumptionAmount}
            onChange={(value) => setEditConsumptionAmount(Number(value) || 0)}
            min={0.01}
            decimalScale={2}
            prefix={`${getCurrencySymbol(contract.currency || 'MVR')} `}
            required
          />
          <TextInput
            label="PO Number"
            value={editConsumptionPoNumber}
            onChange={(event) => setEditConsumptionPoNumber(event.currentTarget.value)}
            required
          />
          <FileInput
            label="Replacement PO Document (optional)"
            placeholder="Upload only if the PO document changed"
            value={editConsumptionFile}
            onChange={setEditConsumptionFile}
          />
          <Text size="sm" c="dimmed">
            New Remaining:{' '}
            {formatCurrency(
              contract.contractValue -
                ((consumptions || []).reduce((sum, item) => sum + item.amount, 0) -
                  (editingConsumption?.amount || 0) +
                  editConsumptionAmount),
              contract.currency || 'MVR'
            )}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditingConsumption(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveConsumption}
              loading={updateConsumption.isPending || uploadDocument.isPending}
              disabled={editConsumptionAmount <= 0 || !editConsumptionPoNumber.trim()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={extensionModalOpen}
        onClose={() => setExtensionModalOpen(false)}
        title="Extend Contract"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Initial end date:{' '}
            {contractInitialEndDate
              ? dayjs(contractInitialEndDate).format('DD MMM YYYY')
              : '—'}
          </Text>
          <Text size="sm" c="dimmed">
            Current end date:{' '}
            {contract.endDate ? dayjs(contract.endDate).format('DD MMM YYYY') : '—'}
          </Text>
          <NumberInput
            label="Extension (days)"
            description="Added to the current end date"
            placeholder="e.g. 30"
            value={extensionDays}
            onChange={(value) => {
              if (value === '') {
                setExtensionDays('');
              } else {
                setExtensionDays(typeof value === 'number' ? value : Number(value));
              }
            }}
            min={1}
            allowDecimal={false}
            required
          />
          {calculatedExtensionEndDate && (
            <Text size="sm">
              New end date:{' '}
              <Text span fw={600}>
                {calculatedExtensionEndDate.format('DD MMM YYYY')}
              </Text>
            </Text>
          )}
          <Textarea
            label="Reason"
            value={extensionReason}
            onChange={(event) => setExtensionReason(event.currentTarget.value)}
            required
          />
          <FileInput
            label="Reference Document"
            placeholder="Upload extension approval/reference"
            value={extensionFile}
            onChange={setExtensionFile}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setExtensionModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateExtension}
              loading={createExtension.isPending || uploadDocument.isPending}
              disabled={extensionDaysNum < 1 || !extensionReason.trim() || !extensionFile}
            >
              Extend
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={revisionModalOpen}
        onClose={() => setRevisionModalOpen(false)}
        title="Revise Contract Value"
      >
        <Stack gap="md">
          <NumberInput
            label="New Contract Value"
            value={revisionValue}
            onChange={(value) => setRevisionValue(Number(value) || 0)}
            min={contract.consumedValue}
            decimalScale={2}
            prefix={`${getCurrencySymbol(contract.currency || 'MVR')} `}
            required
          />
          <Textarea
            label="Reason"
            value={revisionReason}
            onChange={(event) => setRevisionReason(event.currentTarget.value)}
            required
          />
          <FileInput
            label="Reference Document"
            placeholder="Upload revision approval/reference"
            value={revisionFile}
            onChange={setRevisionFile}
            required
          />
          <Text size="sm" c="dimmed">
            New Remaining:{' '}
            {formatCurrency(Math.max(0, revisionValue - contract.consumedValue), contract.currency || 'MVR')}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRevisionModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateRevision}
              loading={createRevision.isPending || uploadDocument.isPending}
              disabled={revisionValue < contract.consumedValue || !revisionReason.trim() || !revisionFile}
            >
              Revise
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

